import React, { useEffect, useState, useMemo } from 'react';
import styled from 'styled-components';
import i18n, {
  getI18nCountryCode,
  handleResponseLanguage,
} from '../../../../i18n';
import { useTranslation } from 'react-i18next';

// Components
import { SearchSelectInput } from 'wg-fe-ui';

// Types
import { Change, Option } from '../../../../types/Form';
import { getEnumByNameREST } from '../legacy/services/apiRouterService';

import countryList from '../constants/countrylist.json';
interface Props {
  name: string;
  withDefault?: boolean;
  onSelected: (arg0: Change) => void;
  value: string;
  error: string;
  prefilled: boolean;
  className: string;
  [x: string]: unknown;
}

const BasicCountryPicker: React.FC<React.PropsWithChildren<Props>> = ({
  name,
  withDefault,
  onSelected,
  value,
  error,
  prefilled,
  className,
  ...otherProps
}) => {
  const { t } = useTranslation();
  const [countryOptions, setCountryOptions] = useState<Option[]>([]);

  useEffect(() => {
    const fetchSupportedCountries = async () => {
      const [resp, status] = await getEnumByNameREST('COUNTRY_CODES');
      if (status !== 200) return;

      const { items: supportedCountries }: { items: string[] } = resp;

      const translatedCountries = supportedCountries
        .filter(
          (countryKey) =>
            handleResponseLanguage(countryList.countries)[countryKey],
        )
        .map((countryKey) => ({
          label: handleResponseLanguage(countryList.countries)[countryKey],
          value: countryKey,
        }));

      setCountryOptions(translatedCountries);
    };

    fetchSupportedCountries();
  }, [i18n.language]);

  const selectedCountry = countryOptions.find(
    (country) => country.value === value,
  );

  const localError = useMemo(() => {
    if (value === 'OTHER') {
      // If brokercloud doesn't know country, it returns 'OTHER'
      return t('required');
    }
    return false;
  }, [value]);

  return (
    <StyledSearchSelectInput
      className={className}
      value={selectedCountry}
      options={countryOptions}
      isSearchable
      {...(withDefault && {
        defaultValue: countryOptions.find(
          ({ value }) => value === getI18nCountryCode(),
        ),
      })}
      onSelected={onSelected}
      placeholder={t('Choose your option')}
      name={name}
      prefilled={prefilled}
      error={error || localError}
    >
      {t('Country')}
    </StyledSearchSelectInput>
  );
};

const StyledSearchSelectInput = styled(SearchSelectInput)`
  label {
    font-weight: 600;
    color: #8990a7;

    .Select__single-value {
      font-weight: 400;
    }
  }

  & .Select__control {
    ${({ error }) => error && `border-color: red; background-color: white;`}
    ${({ prefilled, error }) =>
      prefilled &&
      !error &&
      `border-color: #FFBC0F; background-color: #FFF9EA;`}
  }

  .Select__option {
    line-height: 135%;
  }

  .Select__menu,
  .Select__menu-list {
    max-height: 25rem;
  }
`;

export default BasicCountryPicker;
